import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, Play, Mic } from "lucide-react";
import { Aurora, Typewriter, Magnetic, FireButton, GhostButton } from "@/components/fx";
import { BuildReplay } from "./BuildReplay";
import { HeroBackdrop } from "./HeroBackdrop";
const IDEA = "A booking app for a barbershop that never double-books a barber";

const PromptBar = () => {
  const [n, setN] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setN((x) => (x >= IDEA.length + 30 ? 0 : x + 1)), 55);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="hero-prompt-bar" className="glass mt-8 flex items-center gap-3 rounded-2xl p-2 pl-5">
      <span className="flex-1 truncate font-mono text-sm text-white/85">{IDEA.slice(0, n)}<span className="caret" /></span>
      <span className="hidden rounded-full border border-white/10 p-2 text-white/50 sm:inline-flex"><Mic className="h-4 w-4" /></span>
      <span className="rounded-xl bg-fire px-4 py-2 text-sm font-semibold text-white">Forge it</span>
    </div>
  );
};

export const Hero = () => {
  const reduce = useReducedMotion();
  return (
    <section data-testid="hero-section" className="relative isolate overflow-hidden pb-16 pt-28 md:pb-20 md:pt-32">
      <Aurora />
      <div className="grid-lines absolute inset-0 -z-10" />
      <div className="absolute inset-0 -z-10 opacity-90 [mask-image:linear-gradient(to_bottom,black_70%,transparent)]">
        <HeroBackdrop reduce={reduce} />
      </div>
      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-6 lg:grid-cols-[1.05fr_1fr]">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}>
          <span className="label-mono inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-white/70">
            <span className="live-dot" /> The AI operating system for building software
          </span>
          <h1 className="font-display mt-6 text-4xl font-bold leading-[1.05] tracking-tight text-white sm:text-5xl lg:text-6xl">
            Type a sentence.<br />Get a <Typewriter className="text-fire" words={["website", "web app", "AI agent", "SaaS"]} />
          </h1>
          <p className="mt-6 max-w-xl text-base text-white/65 md:text-lg">
            Plan it. Build it. Test it. Prove it. Ship it. A team of AI agents turns your idea into a real, data-backed product — and runs real checks before calling it done.
          </p>
          <PromptBar />
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Magnetic><FireButton as={Link} to="/signup" data-testid="hero-start-building">Start building <ArrowRight className="h-4 w-4" /></FireButton></Magnetic>
            <GhostButton as="a" href="#replay" data-testid="hero-watch-build"><Play className="h-4 w-4" /> Watch it build</GhostButton>
          </div>
          <div className="mt-8 flex flex-wrap gap-2">
            {["11 specialist agents", "Real checks, never fake ‘done’", "Auto-repair loop", "Live preview in minutes"].map((s) => (
              <span key={s} className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70">{s}</span>
            ))}
          </div>
        </motion.div>
        <motion.div id="replay" initial={{ opacity: 0, y: 40, rotateX: 8 }} animate={{ opacity: 1, y: 0, rotateX: 0 }} transition={{ duration: 0.9, delay: 0.2 }} style={{ perspective: 1000 }}>
          <BuildReplay />
        </motion.div>
      </div>
    </section>
  );
};
